'use client';

import { NextSeo } from 'next-seo';
import { Box, Container, Stack, Typography, useTheme } from '@mui/material';
import { BlogAuthorResponse, BlogPostResponse } from '@/shared/types/common/BlogTypes';
import { WEB_HOST } from '../../../../WebConstants';
import { HubspotBlogForm } from '../components/HubspotBlogForm';
import { PostAuthor } from '../components/PostAuthor';
type BlogPostProps = {
  post: BlogPostResponse & {
    postReadTime: number;
    postAuthor: BlogAuthorResponse;
  };
};
export const BlogPost = ({
  post
}: BlogPostProps) => {
  const theme = useTheme();
  const {
    featuredImage,
    featuredImageAltText,
    htmlTitle,
    postAuthor,
    postBody,
    postReadTime,
    publishDate
  } = post;
  const blogTypographyStyles = {
    '& p': {
      ...theme.typography.bodyLarge,
      lineHeight: '150%',
      my: 0
    },
    '& h2': {
      ...theme.typography.h4
    },
    '& h3': {
      ...theme.typography.h5
    },
    '& h4': {
      ...theme.typography.h5
    },
    '& h5': {
      ...theme.typography.h5
    },
    '& h6': {
      ...theme.typography.h6
    },
    '& a': {
      color: theme.palette.primary.main,
      '&:hover': {
        color: theme.palette.primary.dark
      }
    },
    '& img': {
      display: 'block',
      mx: 'auto',
      my: 2,
      maxWidth: '100%',
      height: 'auto'
    }
  };
  return <>
      <NextSeo canonical={`${WEB_HOST}/${post.slug}`} openGraph={{
      title: post.name,
      description: post.metaDescription,
      images: [{
        url: post.featuredImage,
        alt: ''
      }]
    }} data-sentry-element="NextSeo" data-sentry-source-file="BlogPost.tsx" />
      <Box component="main" sx={{
      py: 6,
      backgroundColor: 'common.white'
    }} data-sentry-element="Box" data-sentry-source-file="BlogPost.tsx">
        <Container component="article" maxWidth={false} // custom width for blog post
      sx={{
        maxWidth: 780
      }} data-sentry-element="Container" data-sentry-source-file="BlogPost.tsx">
          <Stack component="header" spacing={2} sx={{
          alignItems: 'center'
        }} data-sentry-element="Stack" data-sentry-source-file="BlogPost.tsx">
            <Typography component="span" variant="bodySmall" sx={{
            textAlign: 'center',
            display: 'block'
          }} data-sentry-element="Typography" data-sentry-source-file="BlogPost.tsx">{`${postReadTime} min read`}</Typography>
            <Typography component="h1" sx={{
            textAlign: 'center',
            typography: {
              xs: 'h4',
              md: 'h1'
            },
            textWrap: 'balance',
            '@supports not (text-wrap: balance)': {
              whiteSpace: 'pre-line'
            }
          }} data-sentry-element="Typography" data-sentry-source-file="BlogPost.tsx">
              {htmlTitle}
            </Typography>
            <PostAuthor authorName={postAuthor.fullName} authorAvatar={{
            src: postAuthor.avatar
          }} publishDate={publishDate} data-sentry-element="PostAuthor" data-sentry-source-file="BlogPost.tsx" />
          </Stack>
          <Box sx={{
          position: 'relative',
          minHeight: 440,
          mt: 2,
          mb: 6
        }} data-sentry-element="Box" data-sentry-source-file="BlogPost.tsx">
            <img src={featuredImage} alt={featuredImageAltText} style={{
            objectFit: 'cover',
            position: 'absolute',
            height: '100%',
            width: '100%',
            left: 0,
            top: 0,
            right: 0,
            bottom: 0
          }} />
          </Box>
          <Box sx={blogTypographyStyles} dangerouslySetInnerHTML={{
          __html: postBody
        }} data-sentry-element="Box" data-sentry-source-file="BlogPost.tsx" />
        </Container>
        <Container sx={{
        mt: 8
      }} data-sentry-element="Container" data-sentry-source-file="BlogPost.tsx">
          <HubspotBlogForm data-sentry-element="HubspotBlogForm" data-sentry-source-file="BlogPost.tsx" />
        </Container>
      </Box>
    </>;
};